import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { X, Plus, Minus, Trash2, ShoppingCart, CheckCircle } from 'lucide-react'
import { useCartStore } from '../../core/stores/cartStore'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessColor } from '../../core/hooks/useBusinessColor'
import { Card, CardContent } from '../ui/Card'
import Button from '../ui/Button'
import CustomerInfoModal from './CustomerInfoModal'

export default function CartModal() {
  const { slug } = useParams()
  const { isRTL, t } = useTranslation()
  const { color, darker, opacity15 } = useBusinessColor()
  const {
    isOpen,
    closeCart,
    updateQuantity,
    removeItem,
    clearCart,
    getItemsForBusiness,
    getTotalItems,
    getFormattedTotalPrice
  } = useCartStore()

  // Get items only for current business
  const items = slug ? getItemsForBusiness(slug) : []

  const [showCustomerInfo, setShowCustomerInfo] = useState(false)
  const [isOrderPlaced, setIsOrderPlaced] = useState(false)
  const [showClearConfirm, setShowClearConfirm] = useState(false)

  const handlePlaceOrder = () => {
    setShowCustomerInfo(true)
  }

  const handleOrderConfirmed = () => {
    setIsOrderPlaced(true)
    setShowCustomerInfo(false)
    
    // Close modal after 2 seconds
    setTimeout(() => {
      setIsOrderPlaced(false)
      closeCart()
    }, 2000)
  }

  const handleClearCart = () => {
    setShowClearConfirm(true)
  }

  const confirmClearCart = () => {
    clearCart(slug)
    setShowClearConfirm(false)
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={closeCart}
      />

      {/* Cart Sidebar */}
      <div
        className={`fixed top-0 ${isRTL ? 'left-0' : 'right-0'} h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ${isRTL ? 'animate-slide-in-left' : 'animate-slide-in-right'
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-100 bg-white/95 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
          <div className="px-4 sm:px-6">
            <div className="flex justify-between items-center py-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: opacity15 }}
                >
                  <ShoppingCart className="w-5 h-5" style={{ color }} />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-[#1E293B]">
                    {t('shoppingCart')}
                  </h2>
                  <p className="text-xs text-gray-500 leading-tight">
                    {getTotalItems(slug)} {t('itemCount')}
                  </p>
                </div>
              </div>
              <button
                onClick={closeCart}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-stone-100 to-stone-200 rounded-2xl flex items-center justify-center mb-4">
                <ShoppingCart className="w-10 h-10 text-stone-400" />
              </div>
              <h3 className="text-md font-bold text-stone-900 mb-2">
                {t('cartEmpty')}
              </h3>
              <p className="text-stone-600 text-sm">
                {t('addItemsFromMenu')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((cartItem) => (
                <Card key={cartItem.item.id} variant="elevated" className="border-gray-200">
                  <CardContent className="p-3 pb-4 pt-4">
                    <div className="flex gap-3">

                      {/* Item Image */}
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-stone-100 to-stone-200 flex-shrink-0">
                        {cartItem.item.imageUrl ? (
                          <img
                            src={cartItem.item.imageUrl}
                            alt={cartItem.item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ShoppingCart className="w-5 h-5 text-stone-400" />
                          </div>
                        )}
                      </div>

                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1.5">
                 <div className="flex-1 min-w-0 pr-2">
                   <h3 className="font-semibold text-stone-900 text-sm leading-tight mb-0.5 line-clamp-1">
                     {cartItem.item.name}
                   </h3>
                   <p className="text-sm font-bold" style={{ color }}>
                     {cartItem.item.formattedPrice}
                   </p>
                 </div>
                 <button
                   onClick={() => removeItem(cartItem.item.id, slug)}
                   className="w-6 h-6 rounded-md flex items-center justify-center transition-colors flex-shrink-0"
                   style={{ 
                     backgroundColor: opacity15,
                     color: darker,
                   }}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.backgroundColor = color
                     e.currentTarget.style.color = 'white'
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.backgroundColor = opacity15
                     e.currentTarget.style.color = darker
                   }}
                 >
                   <Trash2 className="w-3.5 h-3.5" />
                 </button>
                        </div>

                        {/* Quantity Controls */}
               <div className="flex items-center gap-1.5">
                 <button
                   onClick={() => updateQuantity(cartItem.item.id, cartItem.quantity - 1, slug)}
                   className="w-6 h-6 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                 >
                   <Minus className="w-3 h-3 text-gray-600" />
                 </button>
                 <span className="w-10 h-6 flex items-center justify-center font-semibold text-xs text-stone-900 bg-gray-50 rounded-md border border-gray-200">
                   {cartItem.quantity}
                 </span>
                 <button
                   onClick={() => updateQuantity(cartItem.item.id, cartItem.quantity + 1, slug)}
                   className="w-6 h-6 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                 >
                   <Plus className="w-3 h-3 text-gray-600" />
                 </button>
                 <div className={`flex-1 ${isRTL ? 'text-left' : 'text-right'}`}>
                   <p className="text-sm font-bold text-stone-700">
                     {(cartItem.item.price * cartItem.quantity).toFixed(2)} 
                   </p>
                 </div>
                        </div>
                      </div>

                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {items.length > 0 && (
          <div className="border-t border-gray-200 bg-white p-4 sticky bottom-0 shadow-lg">
            {isOrderPlaced ? (
              <div className="text-center py-3">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                <p className="text-base font-bold text-gray-900 mb-1">
                  {t('orderConfirmed')}
                </p>
                <p className="text-xs text-gray-600">
                  {t('thankYouContactSoon')}
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between py-2 mb-3 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-700">
                    {t('totalAmount')}
                  </span>
                  <span className="text-xl font-bold" style={{ color: darker }}>
                    {getFormattedTotalPrice(slug)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handlePlaceOrder}
                    variant="primary"
                    className="flex-1 h-10 text-xs font-semibold text-white shadow-md"
                    style={{
                      background: `linear-gradient(to right, ${color}, ${darker})`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${darker}, ${color})`
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${color}, ${darker})`
                    }}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {t('confirmOrder')}
                    </span>
                  </Button>

                  <Button
                    onClick={handleClearCart}
                    variant="outline"
                    className="flex-1 h-10 text-xs font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    {t('clearCart')}
                  </Button>
                  
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Customer Info Modal */}
      <CustomerInfoModal
        isOpen={showCustomerInfo}
        onClose={() => setShowCustomerInfo(false)}
        onConfirm={handleOrderConfirmed}
      />

      {/* Clear Cart Confirmation Modal */}
      {showClearConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[140]"
            onClick={() => setShowClearConfirm(false)}
          />
          <div
            className={`fixed top-1/2 left-1/2 w-full max-w-xs bg-white rounded-xl shadow-2xl z-[150] transform -translate-x-1/2 -translate-y-1/2`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: opacity15 }}
                >
                  <Trash2 className="w-5 h-5" style={{ color: darker }} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {t('clearCartConfirm')}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t('clearCartConfirmMessage')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
              <Button
                  onClick={confirmClearCart}
                  variant="primary"
                  className="h-9 text-xs font-semibold text-white"
                  style={{
                    backgroundColor: darker,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = color
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = darker
                  }}
                >
                  {t('clear')}
                </Button>

                <Button
                  onClick={() => setShowClearConfirm(false)}
                  variant="outline"
                  className="h-9 text-xs font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel')}
                </Button>
                
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

